import {
  PresentationContent,
  PresentationModification,
  ToolCall,
  ChatMessage,
  ReferenceSlideImage,
} from "../../../types";
import { ModelConfig } from "../../../modelConfig";
import { buildApiUrl } from "../../../config";
import { AiRequestOptions, isResearchToolName, StreamCallbacks, ToolExecutionPolicy } from "../types";
import {
  readModelError,
  prepareRequestData,
  fetchWithRetry,
  stripMarkdownFences,
} from "../../../utils";
import { withCredentials } from "../../apiClient";
import { withOptionalPptxLangfuseMetadata } from "../../../langfuseTrace";
import { executeTools, isToolAllowed, buildBlockedToolResult } from "../tools";
import { buildProviderToolResultsFollowUpPrompt, getSystemPrompt } from "../promptCatalog";
import { finalizeModelModifications } from "../modificationFinalizer";
import { sanitizeToolResultsForModelOutput } from "../logoCompanyRefs";

export async function queryAnthropic(
  presentationContent: PresentationContent,
  userQuery: string,
  callbacks: StreamCallbacks,
  model: ModelConfig,
  slideImages?: string[],
  chatHistory?: ChatMessage[],
  toolPolicy?: ToolExecutionPolicy,
  options: AiRequestOptions = {}
): Promise<void> {
  const { textMessage } = prepareRequestData(presentationContent, userQuery, chatHistory);
  const systemPrompt = getSystemPrompt(toolPolicy);

  const messages: Array<{ role: string; content: unknown }> = [];

  if (slideImages && slideImages.length > 0) {
    const contentParts: unknown[] = slideImages.map((img) => ({
      type: "image",
      source: { type: "base64", media_type: "image/png", data: img },
    }));
    contentParts.push({ type: "text", text: textMessage });
    messages.push({ role: "user", content: contentParts });
  } else {
    messages.push({ role: "user", content: textMessage });
  }

  try {
    const response = await fetchWithRetry(
      buildApiUrl("/api/ai/anthropic/messages"),
      withOptionalPptxLangfuseMetadata(
        withCredentials({
          method: "POST",
          signal: options.signal,
          body: JSON.stringify({
            model: model.model,
            max_tokens: 16384,
            system: systemPrompt,
            messages,
          }),
        }),
        options.langfuseContext,
        { phase: options.langfusePhase ?? "chat" }
      ),
      2,
      "PPT-Anthropic"
    );

    if (!response.ok) {
      callbacks.onError(await readModelError(response, "Anthropic request failed"));
      return;
    }

    const data = await response.json();
    console.log("[ANTHROPIC] Response:", JSON.stringify(data).slice(0, 500));

    let thinkingText = "";
    let contentText = "";

    if (data.content && Array.isArray(data.content)) {
      for (const block of data.content) {
        if (block.type === "thinking" && block.thinking) {
          thinkingText = block.thinking;
        }
        if (block.type === "text" && block.text) {
          contentText = block.text;
        }
      }
    }

    if (thinkingText) {
      callbacks.onThinking(thinkingText);
    }

    if (!contentText.trim()) {
      callbacks.onError("Empty response from Anthropic");
      return;
    }

    try {
      const toolsMatch = contentText.match(/<tools>([\s\S]*?)<\/tools>/);
      let jsonContent = contentText;
      let toolCalls: ToolCall[] | null = null;

      if (toolsMatch) {
        try {
          toolCalls = JSON.parse(toolsMatch[1].trim()) as ToolCall[];
        } catch (e) {
          console.warn("[ANTHROPIC] Failed to parse XML tool calls:", e);
        }
        jsonContent = contentText.replace(/<tools>[\s\S]*?<\/tools>/, "").trim();
      } else {
        try {
          const tryJson = stripMarkdownFences(contentText);
          const parsed = JSON.parse(tryJson);
          if (parsed.tools && Array.isArray(parsed.tools)) {
            toolCalls = parsed.tools as ToolCall[];
          }
        } catch {
          // Not valid JSON yet
        }
      }

      if (toolCalls && toolCalls.length > 0) {
        const blockedToolResults = toolCalls
          .filter((call) => !isToolAllowed(call.tool, toolPolicy))
          .map((call) =>
            buildBlockedToolResult(
              call.tool,
              `Tool ${call.tool} is disabled for this request context.`
            )
          );
        const allowedToolCalls = toolCalls.filter((call) => isToolAllowed(call.tool, toolPolicy));
        const executedTools =
          allowedToolCalls.length > 0
            ? await executeTools(allowedToolCalls, {
                presentationContent,
                finsterDataSources: toolPolicy?.finsterDataSources,
                toolPolicy,
                agentToolContext: options.agentToolContext,
                langfuseContext: options.langfuseContext,
              })
            : { results: [], referenceImages: [] as ReferenceSlideImage[] };
        const toolResults = [...blockedToolResults, ...executedTools.results];
        const needsFollowUp = blockedToolResults.length > 0 || toolResults.some((r) => r.success);
        if (executedTools.referenceImages.length > 0) {
          console.log(
            `[ANTHROPIC] Captured ${executedTools.referenceImages.length} slide image(s) via get_slide_images (follow-up image injection is OpenAI-first in this pass)`
          );
        }

        for (const result of toolResults.filter((result) => isToolAllowed(result.tool, toolPolicy))) {
          callbacks.onToolCall(
            result.tool,
            result.tool === "get_company_logo"
              ? `resolved=${(result.data as { resolvedCount?: number } | undefined)?.resolvedCount ?? 0} failed=${(result.data as { failedCount?: number } | undefined)?.failedCount ?? 0}`
              : result.tool === "get_slide_images"
                ? `captured_slides=${((result.data as { capturedSlideNumbers?: number[] } | undefined)?.capturedSlideNumbers || []).join(",") || "(none)"}`
                : isResearchToolName(result.tool)
                  ? "Querying..."
                  : ""
          );
        }

        if (callbacks.onToolResult) {
          callbacks.onToolResult(toolResults);
        }

        if (needsFollowUp) {
          console.log("[ANTHROPIC] Making follow-up call with tool results");
          const followUpResponse = await fetchWithRetry(
            buildApiUrl("/api/ai/anthropic/messages"),
            withOptionalPptxLangfuseMetadata(
              withCredentials({
                method: "POST",
                signal: options.signal,
                body: JSON.stringify({
                  model: model.model,
                  max_tokens: 16384,
                  system: systemPrompt,
                  messages: [
                    ...messages,
                    { role: "assistant", content: contentText },
                    {
                      role: "user",
                      content: buildProviderToolResultsFollowUpPrompt({
                        serializedToolResults: JSON.stringify(
                          sanitizeToolResultsForModelOutput(toolResults),
                          null,
                          2
                        ),
                        toolPolicy,
                      }),
                    },
                  ],
                }),
              }),
              options.langfuseContext,
              { phase: "multi_hop_executor" }
            ),
            2,
            "PPT-Anthropic-FollowUp"
          );

          if (followUpResponse.ok) {
            const followUpData = await followUpResponse.json();
            const followUpText = followUpData.content?.[0]?.text || "";
            if (followUpText) {
              jsonContent = followUpText.replace(/<tools>[\s\S]*?<\/tools>/, "").trim();
            }
          }
        }
      }

      const jsonStr = stripMarkdownFences(jsonContent);
      const modifications = JSON.parse(jsonStr) as PresentationModification;
      callbacks.onComplete(finalizeModelModifications(modifications));
    } catch (parseErr) {
      console.error("[ANTHROPIC] JSON parse error:", parseErr);
      callbacks.onError(`Failed to parse Anthropic response: ${parseErr}`);
    }
  } catch (error) {
    callbacks.onError(
      `Anthropic query error: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
